"use client";

import useEmblaCarousel, {
  type UseEmblaCarouselType,
} from "embla-carousel-react";
import * as React from "react";

import { css, cva } from "@/../styled-system/css";
import ArrowLeft from "@/assets/svg/arrow-left.svg";
import ArrowRight from "@/assets/svg/arrow-right.svg";

import Button from "./Button";

type CarouselApi = UseEmblaCarouselType[1];
type UseCarouselParameters = Parameters<typeof useEmblaCarousel>;
type CarouselOptions = UseCarouselParameters[0];
type CarouselPlugin = UseCarouselParameters[1];

type CarouselProps = {
  opts?: CarouselOptions;
  plugins?: CarouselPlugin;
  orientation?: "horizontal" | "vertical";
  setApi?: (api: CarouselApi) => void;
};

type CarouselContextProps = {
  carouselRef: ReturnType<typeof useEmblaCarousel>[0];
  api: ReturnType<typeof useEmblaCarousel>[1];
  scrollPrev: () => void;
  scrollNext: () => void;
  canScrollPrev: boolean;
  canScrollNext: boolean;
} & CarouselProps;

const CarouselContext = React.createContext<CarouselContextProps | null>(null);

function useCarousel() {
  const context = React.useContext(CarouselContext);

  if (!context) {
    throw new Error("useCarousel must be used within a <Carousel />");
  }

  return context;
}

const Carousel = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> &
    CarouselProps & {
      css?: object;
    }
>(
  (
    {
      css: cssProp = {},
      orientation = "horizontal",
      opts,
      setApi,
      plugins,
      children,
      ...props
    },
    ref,
  ) => {
    const className = css({ pos: "relative" }, cssProp);
    const [carouselRef, api] = useEmblaCarousel(
      {
        ...opts,
        axis: orientation === "horizontal" ? "x" : "y",
      },
      plugins,
    );
    const [canScrollPrev, setCanScrollPrev] = React.useState(false);
    const [canScrollNext, setCanScrollNext] = React.useState(false);

    const onSelect = React.useCallback((carousel: CarouselApi) => {
      if (!carousel) {
        return;
      }

      setCanScrollPrev(carousel.canScrollPrev());
      setCanScrollNext(carousel.canScrollNext());
    }, []);

    const scrollPrev = React.useCallback(() => {
      api?.scrollPrev();
    }, [api]);

    const scrollNext = React.useCallback(() => {
      api?.scrollNext();
    }, [api]);

    const handleKeyDown = React.useCallback(
      (event: React.KeyboardEvent<HTMLDivElement>) => {
        if (event.key === "ArrowLeft") {
          event.preventDefault();
          scrollPrev();
        } else if (event.key === "ArrowRight") {
          event.preventDefault();
          scrollNext();
        }
      },
      [scrollPrev, scrollNext],
    );

    React.useEffect(() => {
      if (!api || !setApi) {
        return;
      }

      setApi(api);
    }, [api, setApi]);

    React.useEffect(() => {
      if (!api) {
        return;
      }

      onSelect(api);
      api.on("reInit", onSelect);
      api.on("select", onSelect);

      return () => {
        api?.off("select", onSelect);
      };
    }, [api, onSelect]);

    return (
      <CarouselContext.Provider
        value={{
          carouselRef,
          api: api,
          opts,
          orientation:
            orientation || (opts?.axis === "y" ? "vertical" : "horizontal"),
          scrollPrev,
          scrollNext,
          canScrollPrev,
          canScrollNext,
        }}
      >
        <div
          ref={ref}
          onKeyDownCapture={handleKeyDown}
          className={className}
          role="region"
          aria-roledescription="carousel"
          {...props}
        >
          {children}
        </div>
      </CarouselContext.Provider>
    );
  },
);
Carousel.displayName = "Carousel";

const carouselContentRecipe = cva({
  base: {
    display: "flex",
  },
  variants: {
    orientation: {
      horizontal: {
        mx: "0",
      },
      vertical: {
        mt: "4",
        flexDir: "column",
      },
    },
  },
});

const CarouselContent = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> & { css?: object }
>(({ css: cssProp = {}, ...props }, ref) => {
  const { carouselRef, orientation } = useCarousel();
  const className = css(carouselContentRecipe.raw({ orientation }), cssProp);

  return (
    <div ref={carouselRef} className={css({ overflow: "hidden" })}>
      <div ref={ref} className={className} {...props} />
    </div>
  );
});
CarouselContent.displayName = "CarouselContent";

const carouselItemRecipe = cva({
  base: {
    minW: "0",
    flexShrink: "0",
    flexGrow: "0",
    flexBasis: "full",
  },
  variants: {
    orientation: {
      horizontal: {
        px: "0",
      },
      vertical: {
        pt: "4",
      },
    },
  },
});

const CarouselItem = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> & { css?: object }
>(({ css: cssProp = {}, ...props }, ref) => {
  const { orientation } = useCarousel();
  const className = css(carouselItemRecipe.raw({ orientation }), cssProp);

  return (
    <div
      ref={ref}
      role="group"
      aria-roledescription="slide"
      className={className}
      {...props}
    />
  );
});
CarouselItem.displayName = "CarouselItem";

const carouselPreviousRecipe = cva({
  base: {
    pos: "absolute",
    rounded: "full",
    backgroundColor: "slate.900/40",
    transition: "all 0.2s ease-in-out",
    h: "32px",
    w: "32px",
    sm: {
      h: "40px",
      w: "40px",
    },
    _hover: {
      backgroundColor: "slate.900/80",
    },
    "& .icon": {
      mx: "auto",
      width: "22px",
      sm: {
        width: "26px",
      },
    },
  },
  variants: {
    orientation: {
      horizontal: {
        left: "8px",
        sm: {
          left: "36px",
        },
        top: "50%",
        transform: "translateY(-50%)",
      },
      vertical: {
        top: "48px",
        left: "50%",
        transform: "translateX(50%)",
        rotate: "90deg",
      },
    },
  },
});

const CarouselPrevious = React.forwardRef<
  HTMLButtonElement,
  React.ComponentProps<typeof Button> & { css?: object }
>(({ css: cssProp = {}, ...props }, ref) => {
  const { orientation, scrollPrev, canScrollPrev } = useCarousel();
  const className = css(carouselPreviousRecipe.raw({ orientation }), cssProp);

  return (
    <Button
      ref={ref}
      variant="outline"
      width="icon"
      className={className}
      disabled={!canScrollPrev}
      onClick={scrollPrev}
      {...props}
    >
      <ArrowLeft stroke="white" className="icon" />
      <span
        className={css({
          srOnly: "true",
        })}
      >
        Previous slide
      </span>
    </Button>
  );
});
CarouselPrevious.displayName = "CarouselPrevious";

const carouselNextRecipe = cva({
  base: {
    pos: "absolute",
    rounded: "full",
    backgroundColor: "slate.900/40",
    transition: "all 0.2s ease-in-out",
    h: "32px",
    w: "32px",
    sm: {
      h: "40px",
      w: "40px",
    },
    _hover: {
      backgroundColor: "slate.900/80",
    },
    "& .icon": {
      mx: "auto",
      width: "22px",
      sm: {
        width: "26px",
      },
    },
  },
  variants: {
    orientation: {
      horizontal: {
        right: "8px",
        sm: {
          right: "36px",
        },
        top: "50%",
        transform: "translateY(-50%)",
      },
      vertical: {
        bottom: "48px",
        left: "50%",
        transform: "translateX(50%)",
        rotate: "90deg",
      },
    },
  },
});

const CarouselNext = React.forwardRef<
  HTMLButtonElement,
  React.ComponentProps<typeof Button> & { css?: object }
>(({ css: cssProp = {}, ...props }, ref) => {
  const { orientation, scrollNext, canScrollNext } = useCarousel();
  const className = css(carouselNextRecipe.raw({ orientation }), cssProp);

  return (
    <Button
      ref={ref}
      variant="outline"
      width="icon"
      className={className}
      disabled={!canScrollNext}
      onClick={scrollNext}
      {...props}
    >
      <ArrowRight stroke="white" className="icon" />
      <span
        className={css({
          srOnly: "true",
        })}
      >
        Next slide
      </span>
    </Button>
  );
});
CarouselNext.displayName = "CarouselNext";

export {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
  type CarouselApi,
};
